import { useQueryClient } from '@tanstack/react-query';
import { $api } from './api';
import { components, paths } from '../generated/v1';

export type User = components['schemas']['User'];
export type UserRights = components['schemas']['UserRights'];
export type Role = {
  category: string;
  label: string;
  level: 'none' | 'read' | 'read_write';
};

export type UserData = {
  id: number;
  name: string;
  email?: string | null;
  login?: string;
  lang?: string;
  roles: Role[];
  login_date?: string | null;
  state?: string | null;
  notification_email?: boolean;
  notification_sms?: boolean;
  notification_daily_updates?: boolean;
};

export interface PagedQueryParams {
  page?: number;
  size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc' | null;
  domain?: string;
}

export const useUsersQuery = (params?: paths['/users/user/']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/users/user/', {
    params: {
      query: params,
    },
  });
};

export const useProfileQuery = () => {
  return $api.useQuery('get', '/users/profile');
};

export const usePagedUserQuery = ({ page, size, sort_by, sort_order, domain }: PagedQueryParams) => {
  const query = useUsersQuery({
    sort_by,
    sort_order,
    page,
    size,
    domain,
  });

  const paginatedData = query.data
    ? {
        items: query.data.items,
        total: query.data.total,
        page: query.data.page,
        size: query.data.size,
        pages: query.data.pages,
      }
    : undefined;

  return {
    data: paginatedData,
    isLoading: query.isLoading,
  };
};

export const useUserAccessRightsQuery = () => {
  return $api.useQuery('get', '/users/user/access_rights/');
};

export const useCancelUsers = () => {
  const queryClient = useQueryClient();

  return $api.useMutation('put', '/utility/bulk_action/{model}/cancel', {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['get', '/users/user/'] });
    },
  });
};

// `options` belongs in the *fourth* argument: openapi-react-query's signature is
// useQuery(method, path, init, options). Spreading it into `init` (3rd) would put
// `enabled` into the fetch init and into the query key instead of into react-query,
// i.e. the query would run regardless.
export const useUserQuery = (user_id: number, options?: { enabled?: boolean }) => {
  return $api.useQuery('get', '/users/user/{user_id}', { params: { path: { user_id } } }, options);
};

export const useUserMutations = () => {
  const queryClient = useQueryClient();

  // Accepts an optional `password` in the body: the user is then created and the
  // password set in the same request/transaction. That replaces the old
  // create-then-set-password pair, which could leave a user behind that had no
  // password and therefore could not log in.
  const createMutation = $api.useMutation('post', '/users/user/', {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['get', '/users/user/'] });
    },
  });

  /**
   * @deprecated Do not use for creating users — pass `password` to `createMutation`
   * instead so creation and password are one atomic request. The endpoint stays
   * for backwards compatibility only.
   */
  const setPasswordMutation = $api.useMutation('post', '/users/user/{user_id}/set_initial_password', {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['get', '/users/user/'] });
    },
  });

  const updateMutation = $api.useMutation('put', '/users/user/{user_id}', {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['get', '/users/user/'] });
    },
  });

  return {
    createMutation,
    setPasswordMutation,
    updateMutation,
  };
};
